import React, { useState } from 'react';
import './AddRestaurantModal.css';

interface AddRestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RestaurantFormData) => void;
}

export interface RestaurantFormData {
  // Restaurant Information
  restaurantName: string;
  brandName: string;
  googlePlaceId: string;
  description: string;
  
  // Owner Information
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  
  // Location
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: string;
  longitude: string;
  
  // Restaurant Details
  cuisineTypes: string[];
  priceRange: string;
  openingHours: string;
  seatingCapacity: string;
}

const cuisineOptions = [
  'Italian', 'Chinese', 'Japanese', 'Mexican',
  'Indian', 'Thai', 'American', 'French',
  'Mediterranean', 'Korean', 'Vietnamese', 'Greek',
  'Spanish', 'BBQ', 'Vegan', 'Vegetarian',
  'Seafood', 'Steakhouse'
];

const priceRangeOptions = [
  '$ - Budget Friendly',
  '$$ - Moderate',
  '$$$ - Expensive',
  '$$$$ - Very Expensive'
];

const AddRestaurantModal: React.FC<AddRestaurantModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<RestaurantFormData>({
    restaurantName: '',
    brandName: '',
    googlePlaceId: '',
    description: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    cuisineTypes: [],
    priceRange: '',
    openingHours: '',
    seatingCapacity: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RestaurantFormData, string>>>({});

  if (!isOpen) return null;

  const handleInputChange = (field: keyof RestaurantFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCuisineToggle = (cuisine: string) => {
    setFormData(prev => {
      const currentTypes = prev.cuisineTypes;
      const newTypes = currentTypes.includes(cuisine)
        ? currentTypes.filter(t => t !== cuisine)
        : [...currentTypes, cuisine];
      return { ...prev, cuisineTypes: newTypes };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RestaurantFormData, string>> = {};

    if (!formData.restaurantName.trim()) {
      newErrors.restaurantName = 'Restaurant name is required';
    }
    if (!formData.googlePlaceId.trim()) {
      newErrors.googlePlaceId = 'Google Place ID is required';
    }
    if (!formData.ownerName.trim()) {
      newErrors.ownerName = 'Owner name is required';
    }
    if (!formData.ownerEmail.trim()) {
      newErrors.ownerEmail = 'Owner email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = 'Invalid email format';
    }
    if (!formData.ownerPhone.trim()) {
      newErrors.ownerPhone = 'Owner phone is required';
    }
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }
    if (formData.latitude && isNaN(Number(formData.latitude))) {
      newErrors.latitude = 'Latitude must be a number';
    }
    if (formData.longitude && isNaN(Number(formData.longitude))) {
      newErrors.longitude = 'Longitude must be a number';
    }
    if (formData.cuisineTypes.length === 0) {
      newErrors.cuisineTypes = 'At least one cuisine type is required';
    }
    if (!formData.priceRange) {
      newErrors.priceRange = 'Price range is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      // Reset form
      setFormData({
        restaurantName: '',
        brandName: '',
        googlePlaceId: '',
        description: '',
        ownerName: '',
        ownerEmail: '',
        ownerPhone: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: '',
        longitude: '',
        cuisineTypes: [],
        priceRange: '',
        openingHours: '',
        seatingCapacity: '',
      });
      setErrors({});
    }
  };

  const handleClose = () => {
    setFormData({
      restaurantName: '',
      brandName: '',
      googlePlaceId: '',
      description: '',
      ownerName: '',
      ownerEmail: '',
      ownerPhone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      latitude: '',
      longitude: '',
      cuisineTypes: [],
      priceRange: '',
      openingHours: '',
      seatingCapacity: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Add New Restaurant</h2>
            <p className="modal-description">
              Add a new restaurant partner to your platform. Fill in all required information.
            </p>
          </div>
          <button className="modal-close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Restaurant Information */}
          <div className="modal-section">
            <h3 className="modal-section-title">Restaurant Information</h3>
            <div className="modal-form-grid">
              <div className="modal-form-group">
                <label className="modal-label">
                  Restaurant Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.restaurantName ? 'error' : ''}`}
                  placeholder="Enter restaurant name"
                  value={formData.restaurantName}
                  onChange={(e) => handleInputChange('restaurantName', e.target.value)}
                />
                {errors.restaurantName && (
                  <span className="error-message">{errors.restaurantName}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Brand Name
                </label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="Brand name (optional)"
                  value={formData.brandName}
                  onChange={(e) => handleInputChange('brandName', e.target.value)}
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Google Place ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.googlePlaceId ? 'error' : ''}`}
                  placeholder="ChlJ..."
                  value={formData.googlePlaceId}
                  onChange={(e) => handleInputChange('googlePlaceId', e.target.value)}
                />
                {errors.googlePlaceId && (
                  <span className="error-message">{errors.googlePlaceId}</span>
                )}
              </div>

              <div className="modal-form-group full-width">
                <label className="modal-label">Description</label>
                <textarea
                  className="modal-textarea"
                  placeholder="Brief description of the restaurant"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div className="modal-section">
            <h3 className="modal-section-title">Owner Information</h3>
            <div className="modal-form-grid">
              <div className="modal-form-group">
                <label className="modal-label">
                  Owner Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.ownerName ? 'error' : ''}`}
                  placeholder="Full name"
                  value={formData.ownerName}
                  onChange={(e) => handleInputChange('ownerName', e.target.value)}
                />
                {errors.ownerName && (
                  <span className="error-message">{errors.ownerName}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Owner Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  className={`modal-input ${errors.ownerEmail ? 'error' : ''}`}
                  placeholder="owner@restaurant"
                  value={formData.ownerEmail}
                  onChange={(e) => handleInputChange('ownerEmail', e.target.value)}
                />
                {errors.ownerEmail && (
                  <span className="error-message">{errors.ownerEmail}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Owner Phone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  className={`modal-input ${errors.ownerPhone ? 'error' : ''}`}
                  placeholder="+1 234-567-8900"
                  value={formData.ownerPhone}
                  onChange={(e) => handleInputChange('ownerPhone', e.target.value)}
                />
                {errors.ownerPhone && (
                  <span className="error-message">{errors.ownerPhone}</span>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="modal-section">
            <h3 className="modal-section-title">Location</h3>
            <div className="modal-form-grid">
              <div className="modal-form-group full-width">
                <label className="modal-label">
                  Street Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.streetAddress ? 'error' : ''}`}
                  placeholder="123 Main Street"
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                />
                {errors.streetAddress && (
                  <span className="error-message">{errors.streetAddress}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.city ? 'error' : ''}`}
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
                {errors.city && (
                  <span className="error-message">{errors.city}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  State <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.state ? 'error' : ''}`}
                  placeholder="State"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                />
                {errors.state && (
                  <span className="error-message">{errors.state}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  ZIP Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.zipCode ? 'error' : ''}`}
                  placeholder="12345"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                />
                {errors.zipCode && (
                  <span className="error-message">{errors.zipCode}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Latitude
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.latitude ? 'error' : ''}`}
                  placeholder="e.g., 37.7749"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                />
                {errors.latitude && (
                  <span className="error-message">{errors.latitude}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Longitude
                </label>
                <input
                  type="text"
                  className={`modal-input ${errors.longitude ? 'error' : ''}`}
                  placeholder="e.g., -122.4194"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                />
                {errors.longitude && (
                  <span className="error-message">{errors.longitude}</span>
                )}
              </div>
            </div>
          </div>

          {/* Restaurant Details */}
          <div className="modal-section">
            <h3 className="modal-section-title">Restaurant Details</h3>
            <div className="modal-form-grid">
              <div className="modal-form-group full-width">
                <label className="modal-label">
                  Cuisine Type(s) <span className="required">*</span>
                </label>
                <div className="cuisine-tags">
                  {cuisineOptions.map((cuisine) => (
                    <button
                      key={cuisine}
                      type="button"
                      className={`cuisine-tag ${formData.cuisineTypes.includes(cuisine) ? 'selected' : ''}`}
                      onClick={() => handleCuisineToggle(cuisine)}
                    >
                      {cuisine}
                    </button>
                  ))}
                </div>
                {errors.cuisineTypes && (
                  <span className="error-message">{errors.cuisineTypes}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">
                  Price Range <span className="required">*</span>
                </label>
                <select
                  className={`modal-input modal-select ${errors.priceRange ? 'error' : ''}`}
                  value={formData.priceRange}
                  onChange={(e) => handleInputChange('priceRange', e.target.value)}
                >
                  <option value="">Select price range</option>
                  {priceRangeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {errors.priceRange && (
                  <span className="error-message">{errors.priceRange}</span>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-label">Opening Hours</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="9:00 AM - 10:00 PM"
                  value={formData.openingHours}
                  onChange={(e) => handleInputChange('openingHours', e.target.value)}
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">Seating Capacity</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="50"
                  value={formData.seatingCapacity}
                  onChange={(e) => handleInputChange('seatingCapacity', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="modal-actions">
            <button type="button" className="modal-cancel-btn" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="modal-submit-btn">
              Create Restaurant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRestaurantModal;

